import React, { useState, useEffect, useCallback, useMemo } from "react";
import { PageHeader } from "../../component/Header";
import { H2 } from "../../component/Typography";
import styled from "styled-components";
import { message, Spin, Input } from "antd";
import {
  fetchUsers,
  fetchStats,
  updateLicense,
  resetPassword,
  deleteUser,
  restoreUser,
} from "../../utils/adminApi";
import UserListTable from "./UserListTable";
import LicenseEditModal from "./LicenseEditModal";
import PasswordResetModal from "./PasswordResetModal";
import StatsDashboard from "./StatsDashboard";

function AdminPage({ user }) {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [pwdResetUser, setPwdResetUser] = useState(null);
  const [searchText, setSearchText] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, statsData] = await Promise.all([
        fetchUsers(user),
        fetchStats(user),
      ]);
      setUsers(usersData);
      setStats(statsData);
    } catch (err) {
      console.error(err);
      message.error("데이터 조회 실패: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // 이름, 이메일, 소속에서 부분 일치 (대소문자 무시)
  const filteredUsers = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const fields = [u.name, u.email, u.affiliation].filter(Boolean);
      return fields.some((f) => f.toLowerCase().includes(q));
    });
  }, [users, searchText]);

  const handleEditLicense = (targetUser) => {
    setEditingUser(targetUser);
  };

  const handleSaveLicense = async (payload) => {
    try {
      await updateLicense(user, editingUser.id, payload);
      message.success("라이센스가 업데이트되었습니다.");
      setEditingUser(null);
      await loadAll();
    } catch (err) {
      console.error(err);
      message.error("업데이트 실패: " + (err.response?.data?.message || err.message));
    }
  };

  const handleResetPassword = async (newPassword) => {
    try {
      await resetPassword(user, pwdResetUser.id, newPassword);
      message.success(`${pwdResetUser.email} 비밀번호가 변경되었습니다.`);
      setPwdResetUser(null);
    } catch (err) {
      console.error(err);
      message.error("비밀번호 초기화 실패: " + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (target) => {
    try {
      await deleteUser(user, target.id);
      message.success(`${target.email} 가(이) 삭제되었습니다.`);
      await loadAll();
    } catch (err) {
      console.error(err);
      message.error("삭제 실패: " + (err.response?.data?.message || err.message));
    }
  };

  const handleRestore = async (target) => {
    try {
      await restoreUser(user, target.id);
      message.success(`${target.email} 가(이) 복원되었습니다.`);
      await loadAll();
    } catch (err) {
      console.error(err);
      message.error("복원 실패: " + (err.response?.data?.message || err.message));
    }
  };

  const filterActive = searchText.trim().length > 0;

  return (
    <PageContainer>
      <PageHeader title="관리자" />
      <ContentContainer>
        <H2>회원 관리</H2>
        <SubText>
          로그인 admin: {user?.email}
        </SubText>

        <StatsDashboard stats={stats} />

        <SubText>
          {filterActive
            ? `${filteredUsers.length}명 (전체 ${users.length}명 중 검색됨)`
            : `총 ${users.length}명`}
        </SubText>
        <SearchRow>
          <Input.Search
            placeholder="이름, 이메일, 소속으로 검색"
            allowClear
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 360 }}
          />
        </SearchRow>
        <Spin spinning={loading}>
          <UserListTable
            users={filteredUsers}
            currentAdminId={user?.id}
            onEditLicense={handleEditLicense}
            onResetPassword={setPwdResetUser}
            onDelete={handleDelete}
            onRestore={handleRestore}
          />
        </Spin>
      </ContentContainer>
      {editingUser && (
        <LicenseEditModal
          key={editingUser.id}
          user={editingUser}
          onCancel={() => setEditingUser(null)}
          onSave={handleSaveLicense}
        />
      )}
      {pwdResetUser && (
        <PasswordResetModal
          key={pwdResetUser.id}
          user={pwdResetUser}
          onCancel={() => setPwdResetUser(null)}
          onSave={handleResetPassword}
        />
      )}
    </PageContainer>
  );
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--bg-body);
  max-width: 1400px;
  margin: 0 auto;
  gap: 16px;
`;

const ContentContainer = styled.div`
  padding: 32px 32px 64px;
  min-height: 400px;
`;

const SubText = styled.p`
  color: var(--text-secondary, #888);
  margin: 8px 0 16px;
  font-size: 13px;
`;

const SearchRow = styled.div`
  margin-bottom: 16px;
`;

export default AdminPage;
